import type { JSX } from 'react';
import { MenuDivider, MenuItem } from '@fluentui/react-components';
import {
  ClipboardPasteRegular,
  CopyRegular,
  CutRegular,
  DeleteRegular,
  FolderArrowRight20Regular,
  RenameRegular,
} from '@fluentui/react-icons';

/**
 * Fin COMMUNE des menus contextuels d'un dossier et d'un mail.
 *
 * POURQUOI UN COMPOSANT PARTAGÉ. Ces actions existaient en quatre exemplaires —
 * ligne de dossier et ligne de mail dans l'arbre, dossier et mail dans la page
 * détail — et avaient dérivé : le mail proposait « Copier / Couper / Coller » et
 * « Copier vers… / Déplacer vers… », le dossier seulement « Coller » et
 * « Déplacer… », avec une autre icône, dans un autre ordre, et un « Supprimer »
 * collé au reste dans un cas sur deux. Toute divergence future se corrigera ici,
 * une seule fois, pour les quatre menus.
 *
 * Grammaire imposée par ce composant, du plus anodin au plus lourd de
 * conséquences : modifier l'élément (renommer, presse-papiers) → le placer
 * ailleurs (les « … vers… », qui ouvrent un dialogue) → le supprimer, isolé en
 * dernier. Les actions PROPRES au type d'élément (ouvrir, nouveau sous-dossier,
 * ranger la sélection Outlook) restent au point d'appel, AVANT ce bloc.
 */
export interface ItemActionMenuItemsProps {
  onRename: () => void;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  /** Presse-papiers vide, ou destination invalide (racine de projet). */
  pasteDisabled: boolean;
  /** Infobulle de « Coller » — dit ce qui sera collé. */
  pasteHint?: string;
  onCopyTo: () => void;
  onMoveTo: () => void;
  onDelete: () => void;
}

export function ItemActionMenuItems({
  onRename,
  onCopy,
  onCut,
  onPaste,
  pasteDisabled,
  pasteHint,
  onCopyTo,
  onMoveTo,
  onDelete,
}: ItemActionMenuItemsProps): JSX.Element {
  return (
    <>
      <MenuDivider />
      <MenuItem icon={<RenameRegular />} onClick={onRename}>
        Renommer
      </MenuItem>
      <MenuItem icon={<CopyRegular />} onClick={onCopy}>
        Copier
      </MenuItem>
      <MenuItem icon={<CutRegular />} onClick={onCut}>
        Couper
      </MenuItem>
      <MenuItem
        icon={<ClipboardPasteRegular />}
        disabled={pasteDisabled}
        title={pasteHint}
        onClick={onPaste}
      >
        Coller
      </MenuItem>
      <MenuDivider />
      <MenuItem icon={<CopyRegular />} onClick={onCopyTo}>
        Copier vers…
      </MenuItem>
      <MenuItem icon={<FolderArrowRight20Regular />} onClick={onMoveTo}>
        Déplacer vers…
      </MenuItem>
      <MenuDivider />
      <MenuItem icon={<DeleteRegular />} onClick={onDelete}>
        Supprimer
      </MenuItem>
    </>
  );
}
